/**
 * Roles Page
 * Role CRUD with hierarchy and permission assignment
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { EditOutlined as Edit, DeleteOutline as Delete, Add, SecurityOutlined as Security, Lock, Link, Check, InfoOutlined, MoreVert } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useAuth } from '@/hooks/useAuth';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import type { Role, PermissionAccessLevel } from '@/services/user-management.service';
import { roleLabel } from '@/utils/roleLabel.util';

/** 'none' isn't storable server-side (no on-disk "no access" tier) — it's the
 *  local draft-state and revoke-signal value only, matching what
 *  getRolePermissions returns for an unconfigured module and what
 *  updateRolePermissions accepts to revoke one. Displayed as "Denied" — a
 *  clearer statement of the access outcome than the storage-shaped "None". */
type DraftLevel = PermissionAccessLevel | 'none';

/** Row-action icon button: muted until hovered, then tinted by intent. */
const quietAction = (hover: string) => ({
  color: 'text.disabled',
  '& .MuiSvgIcon-root': { fontSize: 18 },
  '&:hover': { color: hover, backgroundColor: 'action.hover' },
});

const LEVEL_OPTIONS: Array<{
  value: DraftLevel;
  label: string;
  color: string;
}> = [
  { value: 'none', label: 'Denied', color: '#c62828' },
  { value: 'read', label: 'Read', color: '#b26a00' },
  { value: 'edit', label: 'Edit', color: '#1565c0' },
  { value: 'full', label: 'Full', color: '#2e7d32' },
];

const LEVEL_META_BY_VALUE: Record<DraftLevel, (typeof LEVEL_OPTIONS)[number]> = LEVEL_OPTIONS.reduce(
  (acc, opt) => ({ ...acc, [opt.value]: opt }),
  {} as Record<DraftLevel, (typeof LEVEL_OPTIONS)[number]>
);
const levelMeta = (level: DraftLevel) => LEVEL_META_BY_VALUE[level];

/**
 * Per-module label overrides for edit/full — the stored wire value (`'edit'`,
 * `'full'`) and its ordinal meaning (each tier nests the one below it) never
 * change; only the WORDING an owner sees while configuring it does, when the
 * generic Read/Edit/Full ladder doesn't describe what those tiers actually
 * unlock for a specific module.
 *
 * activity.chatLogs specifically: Edit ("Interact") lets a role reply to a
 * customer and self-claim an unassigned chat — require-permission.middleware.
 * ts's orModule fallback on whatsapp.routes.ts's/instagram.routes.ts's /send
 * routes is what makes 'edit' here actually cover sending, not just this
 * label describing an aspiration. Full additionally unlocks reassigning a
 * chat AWAY from someone else and handing back one you don't own — see
 * admin.controller.ts's assignConversation/handBackToBot, both gated on
 * 'full' specifically rather than the router's auto-derived 'edit'.
 */
const LEVEL_LABEL_OVERRIDES: Partial<Record<string, Partial<Record<DraftLevel, string>>>> = {
  'activity.chatLogs': { edit: 'Interact', full: 'Full (+ Assign)' },
};

/** Read/Edit/Full generically, or this module's override for those two tiers. */
function levelLabelFor(moduleKey: string, level: DraftLevel): string {
  return LEVEL_LABEL_OVERRIDES[moduleKey]?.[level] ?? levelMeta(level).label;
}

/** Per-module explainer shown next to the name for modules whose tiers don't
 *  map onto the generic View/Change/Delete-ish intuition Read/Edit/Full
 *  usually implies — currently just chatLogs, where the real unlock at each
 *  tier is genuinely surprising without a hint (Edit = can reply, not just
 *  "can edit a conversation record", which isn't really a concept here). */
const MODULE_TIER_HINTS: Partial<Record<string, string>> = {
  'activity.chatLogs':
    "Read: view conversations only. Interact: also reply and self-claim an unassigned chat. Full: also reassign a chat to/from another team member and hand back one you don't own.",
};

/**
 * Declarative read-dependency registry — a module key on the left needs at
 * least Read access to each module key on the right to fully render (not
 * just to be reachable at all). Informational only here: this drives the
 * warning hint below, it grants nothing by itself. The one live example
 * (activity.chatLogs -> whatsapp) is backed by a REAL enforcement fallback
 * on the backend (require-permission.middleware.ts's `orModule` option on
 * whatsapp-template.routes.ts's two read-only routes — a role with Chat Logs
 * read access already renders past template messages correctly even with
 * WhatsApp Denied); the hint still surfaces here because that fallback only
 * covers already-sent message content, not the rest of what a full WhatsApp
 * grant would unlock (e.g. Settings -> WhatsApp stays denied regardless).
 * Keep this in sync BY HAND with the backend's own `orModule` usages — no
 * shared-types package between the two apps enforces it automatically, same
 * convention as useModuleAccess.ts's ALL_MODULE_KEYS/routeToModuleMap.
 */
const MODULE_READ_DEPENDENCIES: Record<string, string[]> = {
  'activity.chatLogs': ['whatsapp'],
};

const Roles: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // GET/PUT /roles/:roleId/permissions are gated by requireTenantOwner on the
  // backend specifically — a deliberate privilege boundary on top of the
  // general userManagement module access, not something requirePermission's
  // tier alone protects (see user-management.routes.ts's own comment: "A
  // tenant_admin editing role permissions... is exactly the privilege
  // boundary requireTenantAdmin alone doesn't protect"). Without this check
  // a tenant_admin would see the button, click it, and get a confusing 403.
  const canManagePermissions = user?.role === 'tenant_owner' || user?.role === 'superadmin';
  const { hasPermission } = usePermissionAccess();
  const canEditRoles = hasPermission('userManagement', 'edit');
  const canDeleteRoles = hasPermission('userManagement', 'full');

  // Row ⋮ menu — open state and target kept apart so the items don't re-render
  // without a role during MUI's fade-out (same reason as Users.tsx).
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRole, setMenuRole] = useState<Role | null>(null);
  const openRowMenu = (anchor: HTMLElement, role: Role) => {
    setMenuRole(role);
    setMenuAnchor(anchor);
  };
  const runRowAction = (action: (role: Role) => void) => {
    const target = menuRole;
    setMenuAnchor(null);
    if (target) action(target);
  };

  const {
    roles,
    modules,
    rolePermissions,
    loading,
    error,
    success,
    loadRoles,
    createRole,
    updateRole,
    deleteRole,
    loadModules,
    loadRolePermissions,
    updateRolePermissions,
    clearMessages,
  } = useUserManagement();

  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    parentRoleId: '',
    order: 0,
  });
  const [permissionsDraft, setPermissionsDraft] = useState<Record<string, DraftLevel>>({});

  // `loading` from the hook is a single flag shared by every operation —
  // loadRoles and loadModules run concurrently on mount, so whichever
  // resolves first flips it back to false while the other is still in
  // flight, and the table briefly rendered "No roles found" before roles
  // actually landed. Track the initial roles fetch separately so the empty
  // state only shows once loadRoles itself has actually settled.
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await loadRoles();
      } finally {
        setRolesLoading(false);
      }
    })();
    loadModules();
  }, [loadRoles, loadModules]);

  const handleOpenCreate = () => {
    setFormData({ name: '', key: '', description: '', parentRoleId: '', order: 0 });
    setSelectedRole(null);
    setDialogMode('create');
    clearMessages();
  };

  const handleOpenEdit = (role: Role) => {
    setFormData({
      name: role.name,
      key: role.key,
      description: role.description || '',
      parentRoleId: role.parentRoleId?._id || '',
      order: role.order,
    });
    setSelectedRole(role);
    setDialogMode('edit');
    clearMessages();
  };

  const handleOpenDelete = (role: Role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
    clearMessages();
  };

  const handleOpenPermissions = async (role: Role) => {
    setSelectedRole(role);
    clearMessages();
    try {
      await loadRolePermissions(role._id);
      setPermissionsDialogOpen(true);
    } catch (err) {
      // Error state already set by the hook (shown via the Alert above) —
      // just don't open the dialog on stale/missing data, and don't leave
      // this an unhandled rejection.
    }
  };

  // rolePermissions only ever lists plan-ENABLED modules (backend deliberately
  // hides disabled ones from the editable list rather than showing a stale
  // grant that would silently vanish on save — see updateRolePermissions'
  // own comment). Seed the draft from it whenever it (re)loads for the
  // currently-open role.
  useEffect(() => {
    if (rolePermissions && selectedRole && rolePermissions.roleId === selectedRole._id) {
      const draft: Record<string, DraftLevel> = {};
      rolePermissions.permissions.forEach((p) => {
        draft[p.module] = p.accessLevel;
      });
      setPermissionsDraft(draft);
    }
  }, [rolePermissions, selectedRole]);

  // Full module list for the matrix, plan-disabled ones included but locked —
  // shows an owner WHY a module can't be granted yet instead of it just
  // silently not appearing (which would look like a bug, not a plan limit).
  // Sorted alphabetically by display name so the ~30-row matrix is scannable
  // by label rather than following MODULE_REGISTRY's arbitrary insertion
  // order, which mixed unrelated modules together with no visible logic.
  const matrixRows = useMemo(() => {
    return [...modules].sort((a, b) => a.name.localeCompare(b.name));
  }, [modules]);

  const handleSubmit = async () => {
    try {
      // exactOptionalPropertyTypes is on — spread in optional keys only when
      // present rather than assigning `undefined` to them.
      if (dialogMode === 'create') {
        await createRole({
          name: formData.name,
          key: formData.key,
          order: formData.order,
          ...(formData.description ? { description: formData.description } : {}),
          ...(formData.parentRoleId ? { parentRoleId: formData.parentRoleId } : {}),
        });
      } else if (selectedRole) {
        await updateRole(selectedRole._id, {
          name: formData.name,
          order: formData.order,
          ...(formData.description ? { description: formData.description } : {}),
          ...(formData.parentRoleId ? { parentRoleId: formData.parentRoleId } : {}),
        });
      }
      setDialogMode(null);
      setTimeout(() => clearMessages(), 3000);
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleDelete = async () => {
    if (selectedRole) {
      try {
        await deleteRole(selectedRole._id);
        setDeleteDialogOpen(false);
        setTimeout(() => clearMessages(), 3000);
      } catch (err) {
        // Error handled by hook
      }
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    try {
      // 'none' must still be SENT (not filtered out) — updateRolePermissions
      // explicitly treats it as the revoke signal for that module (deletes
      // it from the stored array), never writes it to the schema. Omitting
      // it here would silently no-op every "turn this module off" edit,
      // since the backend only touches modules present in the payload —
      // absence means "leave whatever's already stored alone," not "clear
      // it." Only exclude plan-disabled modules (can't grant/revoke what
      // isn't in the plan; their stored grant must survive untouched).
      const payload = matrixRows
        .filter((m) => m.enabled)
        .map((m) => ({ module: m.key, accessLevel: permissionsDraft[m.key] || 'none' }));
      await updateRolePermissions(selectedRole._id, payload);
      setPermissionsDialogOpen(false);
      setTimeout(() => clearMessages(), 3000);
    } catch (err) {
      // Error handled by hook
    }
  };

  // tenant_user isn't wired to any backend route yet (see role-sync.util.ts's
  // module comment) and isn't assignable from Users.tsx either — hide it from
  // both the main list AND the parent-role picker.
  const visibleRoles = roles.filter((r) => r.key !== 'tenant_user');
  const availableParentRoles = visibleRoles.filter((r) => !selectedRole || r._id !== selectedRole._id);

  // Name/description/hierarchy are locked for every system role (owner,
  // admin, user — all seeded isSystemRole:true, matches the backend's
  // updateRole guard). Permissions are a narrower restriction: only
  // tenant_owner's are locked (an owner has implicit full access regardless
  // of what's stored, so there's nothing meaningful to configure) —
  // tenant_admin's permissions ARE real and owner-configurable, matching
  // updateRolePermissions' own guard on the backend.
  const isDetailsLocked = (role: Role) => role.isSystemRole || role.key === 'tenant_owner';
  const isPermissionsLocked = (role: Role) => role.key === 'tenant_owner' || !canManagePermissions;

  // Which menu items this viewer gets at all (backend tiers: POST/PUT 'edit',
  // DELETE 'full', permissions owner-only), and whether a row has any usable one.
  const showActions = canManagePermissions || canEditRoles || canDeleteRoles;
  const hasRowAction = (role: Role) =>
    !isPermissionsLocked(role) || (!isDetailsLocked(role) && (canEditRoles || canDeleteRoles));

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Roles"
          action={
            canEditRoles ? (
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
                Create Role
              </Button>
            ) : null
          }
        />
        <CardContent>
          {(rolesLoading || loading) && !dialogMode && !permissionsDialogOpen ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Parent Role</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>System Role</TableCell>
                    {showActions && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRoles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showActions ? 7 : 6} align="center">
                        No roles found
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRoles.map((role) => (
                      <TableRow key={role._id}>
                        <TableCell>{roleLabel(role.name)}</TableCell>
                        <TableCell>
                          <Typography
                            component="code"
                            variant="caption"
                            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'text.secondary' }}
                          >
                            {role.key}
                          </Typography>
                        </TableCell>
                        <TableCell>{role.parentRoleId?.name ? roleLabel(role.parentRoleId.name) : '-'}</TableCell>
                        <TableCell>{role.level}</TableCell>
                        <TableCell>
                          {role.key === 'tenant_owner' ? (
                            <Chip label="Full access" size="small" color="primary" />
                          ) : role.permissions && role.permissions.length > 0 ? (
                            <Chip label={`${role.permissions.length} modules`} size="small" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {role.isSystemRole ? (
                            <Chip label="System" size="small" color="primary" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        {showActions && (
                          <TableCell>
                            <Tooltip title={hasRowAction(role) ? 'Actions' : 'Nothing to change on this role'}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Actions for ${roleLabel(role.name)}`}
                                  aria-haspopup="menu"
                                  onClick={(e) => openRowMenu(e.currentTarget, role)}
                                  disabled={!hasRowAction(role)}
                                  sx={quietAction('primary.main')}
                                >
                                  <MoreVert />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {canManagePermissions && (
          <MenuItem
            disabled={!menuRole || isPermissionsLocked(menuRole)}
            onClick={() => runRowAction(handleOpenPermissions)}
          >
            <ListItemIcon>
              <Security fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Permissions"
              secondary={menuRole?.key === 'tenant_owner' ? 'Owner always has full access' : undefined}
            />
          </MenuItem>
        )}
        {canEditRoles && (
          <MenuItem disabled={!menuRole || isDetailsLocked(menuRole)} onClick={() => runRowAction(handleOpenEdit)}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Edit"
              secondary={menuRole && isDetailsLocked(menuRole) ? 'System role' : undefined}
            />
          </MenuItem>
        )}
        {canDeleteRoles && (
          <MenuItem
            disabled={!menuRole || isDetailsLocked(menuRole)}
            onClick={() => runRowAction(handleOpenDelete)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Delete"
              secondary={menuRole && isDetailsLocked(menuRole) ? 'System role' : undefined}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogMode !== null} onClose={() => setDialogMode(null)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Create Role' : 'Edit Role'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Role Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Role Key"
              value={formData.key}
              onChange={(e) =>
                dialogMode === 'create' &&
                setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })
              }
              disabled={dialogMode === 'edit'}
              required={dialogMode === 'create'}
              fullWidth
              helperText="Unique identifier (lowercase, underscores) — cannot be changed after creation"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Parent Role</InputLabel>
              <Select
                value={formData.parentRoleId}
                label="Parent Role"
                onChange={(e) => setFormData({ ...formData, parentRoleId: e.target.value })}
              >
                <MenuItem value="">None (Top Level)</MenuItem>
                {availableParentRoles.map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    {roleLabel(role.name)} (Level {role.level})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Order"
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              fullWidth
              helperText="Display order within same level"
            />
            {dialogMode === 'create' && (
              <Alert severity="info">
                New roles start with no module access. Use "Manage Permissions" after creating it to grant access.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name || (dialogMode === 'create' && !formData.key)}
          >
            {loading ? <CircularProgress size={20} /> : dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete role "{selectedRole?.name}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Matrix Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Manage Permissions — {selectedRole ? roleLabel(selectedRole.name) : ''}
        </DialogTitle>
        <DialogContent sx={{ px: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, px: 3 }}>
            Each tier includes everything below it — Full includes Edit, Edit includes Read.
          </Typography>
          {!rolePermissions ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {matrixRows.map((m, idx) => {
                const level = levelMeta(permissionsDraft[m.key] || 'none');
                // Unsatisfied read-dependencies for this row — only relevant
                // once this module itself has some access granted (a Denied
                // module has nothing to render, so a dependency warning
                // would just be noise).
                const unmetDeps =
                  level.value !== 'none'
                    ? (MODULE_READ_DEPENDENCIES[m.key] || []).filter(
                        (depKey) => (permissionsDraft[depKey] || 'none') === 'none'
                      )
                    : [];
                return (
                  <Box
                    key={m.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      px: 3,
                      py: 1.75,
                      borderTop: idx === 0 ? '1px solid' : 'none',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: m.enabled ? 'text.primary' : 'text.disabled' }}
                      noWrap
                    >
                      {m.name}
                      {!m.enabled && (
                        <Tooltip title="Not available on the current plan">
                          <Lock
                            fontSize="inherit"
                            sx={{ ml: 0.75, verticalAlign: 'middle', color: 'text.disabled' }}
                          />
                        </Tooltip>
                      )}
                      {MODULE_TIER_HINTS[m.key] && (
                        <Tooltip title={MODULE_TIER_HINTS[m.key]}>
                          <InfoOutlined
                            fontSize="inherit"
                            sx={{ ml: 0.75, verticalAlign: 'middle', color: 'text.disabled' }}
                          />
                        </Tooltip>
                      )}
                      {unmetDeps.length > 0 && (
                        <Tooltip
                          title={`Needs Read access to ${unmetDeps
                            .map((depKey) => matrixRows.find((r) => r.key === depKey)?.name || depKey)
                            .join(', ')} to fully render — currently Denied`}
                        >
                          <Link
                            fontSize="inherit"
                            sx={{ ml: 0.75, verticalAlign: 'middle', color: 'warning.main' }}
                          />
                        </Tooltip>
                      )}
                    </Typography>
                    {m.enabled ? (
                      <Select
                        size="small"
                        value={permissionsDraft[m.key] || 'none'}
                        onChange={(e) =>
                          setPermissionsDraft((prev) => ({ ...prev, [m.key]: e.target.value as DraftLevel }))
                        }
                        // Default renderValue clones the selected MenuItem's
                        // children into the closed box — that would carry
                        // the checkmark along with it. Render just the label
                        // here so the check only ever appears in the open
                        // menu, next to the selected option.
                        renderValue={(value) => levelLabelFor(m.key, value as DraftLevel)}
                        sx={{
                          // Wide enough for the longest override label
                          // ("Full (+ Assign)", activity.chatLogs) without
                          // wrapping/truncating — a plain fixed floor is fine
                          // since MUI's Select only ever grows past minWidth,
                          // never shrinks below the content it's given.
                          minWidth: 168,
                          bgcolor: 'background.paper',
                          '& .MuiSelect-select': { color: level.color, fontWeight: 600, py: 0.625 },
                        }}
                      >
                        {LEVEL_OPTIONS.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            value={opt.value}
                            sx={{
                              color: opt.color,
                              fontWeight: 600,
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 2,
                            }}
                          >
                            {levelLabelFor(m.key, opt.value)}
                            {opt.value === (permissionsDraft[m.key] || 'none') && (
                              <Check fontSize="small" sx={{ color: opt.color }} />
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        icon={<Lock sx={{ fontSize: 13 }} />}
                        label="Upgrade"
                        size="small"
                        clickable
                        onClick={() => navigate('/admin/billing/active-plan')}
                        sx={{
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: '0.6875rem',
                          letterSpacing: 0.4,
                          '& .MuiChip-icon': { color: 'text.secondary', ml: 0.5, mr: -0.25 },
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '& .MuiChip-icon': { color: 'primary.contrastText' },
                          },
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Changes apply to everyone with this role.
          </Typography>
          <Box display="flex" gap={1}>
            <Button onClick={() => setPermissionsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSavePermissions}
              variant="contained"
              color="primary"
              disableElevation
              disabled={loading || !rolePermissions}
            >
              {loading ? <CircularProgress size={20} /> : 'Save Permissions'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Roles;
